'use client'

import type { CSSProperties } from 'react'

interface ConfettiPiece {
  left: string
  dx: number
  up: number
  down: number
  rot: number
  delay: number
  size: number
  color: string
}

/* Deterministic scatter — sky / yellow / ink squares popping off the panel's
   top edge, per the concept clip. No randomness: stable across renders. */
const PIECES: readonly ConfettiPiece[] = [
  { left: '30%', dx: -70, up: -90, down: 60, rot: -220, delay: 0, size: 10, color: 'var(--yellow)' },
  { left: '38%', dx: -30, up: -120, down: 80, rot: 160, delay: 0.05, size: 8, color: 'var(--sky)' },
  { left: '46%', dx: 20, up: -70, down: 50, rot: -140, delay: 0.02, size: 12, color: 'var(--ink)' },
  { left: '52%', dx: -55, up: -100, down: 90, rot: 240, delay: 0.08, size: 9, color: 'var(--sky)' },
  { left: '58%', dx: 45, up: -130, down: 70, rot: -190, delay: 0.04, size: 11, color: 'var(--yellow)' },
  { left: '64%', dx: 80, up: -80, down: 55, rot: 200, delay: 0.1, size: 8, color: 'var(--ink)' },
  { left: '70%', dx: -25, up: -110, down: 85, rot: -260, delay: 0.01, size: 10, color: 'var(--sky)' },
  { left: '76%', dx: 60, up: -95, down: 65, rot: 180, delay: 0.07, size: 13, color: 'var(--yellow)' },
  { left: '82%', dx: 30, up: -140, down: 95, rot: -160, delay: 0.03, size: 9, color: 'var(--ink)' },
  { left: '88%', dx: 90, up: -75, down: 50, rot: 220, delay: 0.09, size: 10, color: 'var(--sky)' },
  { left: '93%', dx: 55, up: -115, down: 75, rot: -200, delay: 0.06, size: 8, color: 'var(--yellow)' },
  { left: '97%', dx: 100, up: -90, down: 60, rot: 260, delay: 0.12, size: 11, color: 'var(--sky)' },
  { left: '85%', dx: 15, up: -60, down: 40, rot: 140, delay: 0.14, size: 7, color: 'var(--yellow)' },
  { left: '60%', dx: -90, up: -85, down: 70, rot: -240, delay: 0.11, size: 9, color: 'var(--ink)' },
  { left: '42%', dx: 70, up: -105, down: 85, rot: 190, delay: 0.13, size: 8, color: 'var(--sky)' },
  { left: '74%', dx: -50, up: -125, down: 60, rot: -180, delay: 0.15, size: 12, color: 'var(--yellow)' }
] as const

/** One-shot celebration burst; plays on mount (remounts re-arm it). */
export function ConfettiBurst() {
  return (
    <div aria-hidden style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
      {PIECES.map((p, i) => (
        <span
          key={i}
          className="st-confetti-piece"
          style={
            {
              left: p.left,
              width: p.size,
              height: p.size,
              background: p.color,
              '--cf-dx': `${p.dx}px`,
              '--cf-up': `${p.up}px`,
              '--cf-down': `${p.down}px`,
              '--cf-rot': `${p.rot}deg`,
              '--cf-delay': `${p.delay}s`
            } as CSSProperties
          }
        />
      ))}
    </div>
  )
}
